import React, { useState, useEffect } from 'react';
import './App.css'; // Keep the basic styling

function App() {
  const storeInfo = {
    name: '온장',
    tagline: '정성을 다해 만드는 맛간장',
    intro: '',
    kakaoTalkId: 'onjang',
    kakaoPlusUrl: 'http://pf.kakao.com/_ekLIX',
    hoursLabel: '주문 가능 시간',
    hours: '평일 10시 ~ 18시',
    instagramUrl: '',
    orderNote: '주문은 카카오톡 채널로 부탁드립니다.',
  };

  const [recipes, setRecipes] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [openedRecipeKey, setOpenedRecipeKey] = useState(null);
  const [isNoticeOpen, setIsNoticeOpen] = useState(false);

  const notice = {
    title: '공지사항',
    body: '당분간 주문은 카카오톡 채널을 통해 부탁드립니다.\n레시피는 준비되는 대로 하나씩 추가해드릴 예정입니다.\n\n감사합니다.',
    updatedAt: '2026-02-24',
  };

  const fetchRecipes = async () => {
    const response = await fetch('/recipes.json');
    const text = await response.text();
    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch (e) {
      throw new Error(`서버 응답 형식이 올바르지 않습니다. 서버를 다시 켜주세요. (${response.status})`);
    }

    if (!response.ok) {
      throw new Error(data.error || `목록을 불러오지 못했습니다. (${response.status})`);
    }

    setRecipes(data.data || []);
  };

  useEffect(() => {
    fetchRecipes()
      .catch((fetchError) => {
        console.error('Failed to fetch recipes:', fetchError);
        setError(fetchError.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (openedRecipeKey === null) return;
    const exists = recipes.some((recipe, index) => {
      const recipeKey = String(recipe.id ?? `${recipe.title}-${index}`);
      return recipeKey === openedRecipeKey;
    });
    if (!exists) {
      setOpenedRecipeKey(null);
    }
  }, [recipes, openedRecipeKey]);

  const toggleRecipe = (recipeKey) => {
    setOpenedRecipeKey((prev) => (prev === recipeKey ? null : recipeKey));
  };

  const toLines = (text) => (text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  const normalizeStep = (line) => line.replace(/^\d+\s*[.)]?\s*/, '');

  if (loading) {
    return <div className="App">레시피를 불러오는 중...</div>;
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>온장</h1>
      </header>
      <main>
        <section className="store-info" aria-label="가게 정보">
          <div className="store-info-header">
            <h2>{storeInfo.name}</h2>
            {storeInfo.tagline && <p className="store-tagline">{storeInfo.tagline}</p>}
          </div>

          {storeInfo.intro && <p className="store-description">{storeInfo.intro}</p>}

          <div className="store-info-grid">
            {storeInfo.kakaoTalkId && (
              <div className="store-info-item">
                <strong>카카오톡 ID</strong>
                <span>{storeInfo.kakaoTalkId}</span>
              </div>
            )}
            {(storeInfo.hoursLabel || storeInfo.hours) && (
              <div className="store-info-item">
                <strong>{storeInfo.hoursLabel}</strong>
                <span>{storeInfo.hours}</span>
              </div>
            )}
          </div>

          {(storeInfo.instagramUrl || storeInfo.kakaoPlusUrl || storeInfo.orderNote) && (
            <div className="store-links">
              {storeInfo.instagramUrl && (
                <a
                  className="store-link-btn"
                  href={storeInfo.instagramUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  인스타그램
                </a>
              )}
              {storeInfo.kakaoPlusUrl && (
                <a
                  className="store-link-btn"
                  href={storeInfo.kakaoPlusUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  카카오톡 플러스친구
                </a>
              )}
              {storeInfo.orderNote && (
                <span className="store-order-note-inline">{storeInfo.orderNote}</span>
              )}
            </div>
          )}
        </section>

        <section className="notice-section" aria-label="공지사항">
          <div className={`notice-card ${isNoticeOpen ? 'is-open' : ''}`}>
            <div className="recipe-title-btn">
              <span>{notice.title}</span>
              <button
                type="button"
                className="recipe-toggle-btn"
                onClick={() => setIsNoticeOpen((prev) => !prev)}
                aria-label={`${notice.title} ${isNoticeOpen ? '닫기' : '열기'}`}
              >
                <span className="recipe-toggle-mark">{isNoticeOpen ? '−' : '+'}</span>
              </button>
            </div>
            <div className={`notice-details ${isNoticeOpen ? 'open' : ''}`} aria-hidden={!isNoticeOpen}>
              {toLines(notice.body).map((line, idx) => (
                <p key={`notice-line-${idx}`}>{line}</p>
              ))}
            </div>
            {notice.updatedAt && (
              <div className="notice-updated">수정일: {notice.updatedAt}</div>
            )}
          </div>
        </section>

        {error && <p className="error page-error">오류: {error}</p>}

        <div className="recipe-list">
          {recipes.length > 0 ? (
            recipes.map((recipe, index) => {
              const recipeKey = String(recipe.id ?? `${recipe.title}-${index}`);
              const isOpen = openedRecipeKey === recipeKey;
              return (
              <div key={recipeKey} className={`recipe-card ${isOpen ? 'is-open' : ''}`}>
                <div className="recipe-title-btn">
                  <span>{recipe.title}</span>
                  <button
                    type="button"
                    className="recipe-toggle-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleRecipe(recipeKey);
                    }}
                    aria-label={`${recipe.title} ${isOpen ? '닫기' : '열기'}`}
                  >
                    <span className="recipe-toggle-mark">{isOpen ? '−' : '+'}</span>
                  </button>
                </div>

                <div className={`recipe-details ${isOpen ? 'open' : ''}`} aria-hidden={!isOpen}>
                    {recipe.image && <img src={recipe.image} alt={recipe.title} className="recipe-image" />}
                    <h3>재료:</h3>
                    <ul className="recipe-list-text">
                      {toLines(recipe.ingredients).map((line, idx) => (
                        <li key={`ing-${recipeKey}-${idx}`}>{line}</li>
                      ))}
                    </ul>
                    <h3>조리법:</h3>
                    <ol className="recipe-list-text recipe-steps">
                      {toLines(recipe.instructions).map((line, idx) => (
                        <li key={`step-${recipeKey}-${idx}`}>{normalizeStep(line)}</li>
                      ))}
                    </ol>
                </div>
              </div>
            )})
          ) : (
            <p>아직 등록된 레시피가 없습니다.</p>
          )}
        </div>
      </main>
      <footer className="site-footer">
        <small>© 2026 온장 · 제작: yoonna</small>
      </footer>
    </div>
  );
}

export default App;
